import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Chip
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  SwapVert,
  Timeline,
  Article,
  Bookmark,
  BookmarkBorder,
  ArrowUpward,
  ArrowDownward,
  Refresh
} from '@mui/icons-material';
import CryptoPriceOverview from './CryptoPriceOverview';
import './Exchange.css';

// Mock data for charts with different cryptocurrencies
const generateCandlestickData = (symbol = 'ETH', days = 30) => {
  const data = [];

  // Base prices for different cryptocurrencies
  const basePrices = {
    'ETH': 3500,
    'BTC': 62000,
    'SOL': 125,
    'AVAX': 36,
    'MATIC': 0.9,
    'LINK': 18.5
  };

  // Volatility profiles for different cryptocurrencies
  const volatilityProfiles = {
    'ETH': 0.08,
    'BTC': 0.06,
    'SOL': 0.12,
    'AVAX': 0.10,
    'MATIC': 0.15,
    'LINK': 0.09
  };

  // Trend biases (positive means upward trend, negative means downward)
  const trendBiases = {
    'ETH': 0.02,
    'BTC': -0.01,
    'SOL': 0.04,
    'AVAX': 0.03,
    'MATIC': -0.02,
    'LINK': 0.01
  };

  let price = basePrices[symbol] || 3500;
  const volatility = volatilityProfiles[symbol] || 0.1;
  const trendBias = trendBiases[symbol] || 0;

  for (let i = 0; i < days; i++) {
    // Add some randomness but with the trend bias
    const dailyBias = trendBias * (1 + (Math.random() - 0.5) * 0.5);
    const dailyVolatility = volatility * (1 + (Math.random() - 0.5) * 0.3);

    const open = price;
    // Close price has a bias in the trend direction
    const close = open * (1 + (Math.random() - 0.5 + dailyBias) * dailyVolatility);

    // High and low with more extreme values for volatile assets
    const wickFactor = volatility * 2;
    const high = Math.max(open, close) * (1 + Math.random() * wickFactor);
    const low = Math.min(open, close) * (1 - Math.random() * wickFactor);

    // Generate volume that correlates with price movement
    const priceChange = Math.abs(close - open) / open;
    const volumeBase = basePrices[symbol] * 0.3;
    const volume = volumeBase * (1 + priceChange * 10) * (Math.random() * 0.5 + 0.75);

    data.push({
      date: new Date(Date.now() - (days - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      timestamp: Date.now() - (days - i) * 24 * 60 * 60 * 1000,
      open,
      high,
      low,
      close,
      volume: Math.floor(volume)
    });

    price = close;
  }

  return data;
};

// Mock trading pairs
const tradingPairs = [
  { id: 'eth-usdt', name: 'ETH/USDT', price: 3521.45, change: 2.34 },
  { id: 'btc-usdt', name: 'BTC/USDT', price: 62145.78, change: -1.23 },
  { id: 'sol-usdt', name: 'SOL/USDT', price: 124.56, change: 5.67 },
  { id: 'avax-usdt', name: 'AVAX/USDT', price: 35.78, change: 3.21 },
  { id: 'matic-usdt', name: 'MATIC/USDT', price: 0.89, change: -2.45 },
  { id: 'link-usdt', name: 'LINK/USDT', price: 18.34, change: 1.56 }
];

// Mock news data
const newsItems = [
  {
    id: 1,
    title: 'Ethereum Layer 2 Solutions See Record Growth',
    source: 'CryptoNews',
    time: '2 hours ago',
    important: true
  },
  {
    id: 2,
    title: 'Bitcoin Mining Difficulty Reaches All-Time High',
    source: 'BlockchainReport',
    time: '5 hours ago'
  },
  {
    id: 3,
    title: 'Major Bank Launches Crypto Custody Service',
    source: 'FinanceDaily',
    time: '8 hours ago'
  }
];

// Mock order book data
const generateOrderBook = () => {
  const asks = [];
  const bids = [];

  let askPrice = 3550;
  let bidPrice = 3500;

  for (let i = 0; i < 10; i++) {
    asks.push({
      price: askPrice + i * 5,
      amount: Math.random() * 10 + 0.5,
      total: 0
    });

    bids.push({
      price: bidPrice - i * 5,
      amount: Math.random() * 10 + 0.5,
      total: 0
    });
  }

  // Calculate totals
  let askTotal = 0;
  let bidTotal = 0;

  asks.forEach(ask => {
    askTotal += ask.amount;
    ask.total = askTotal;
  });

  bids.forEach(bid => {
    bidTotal += bid.amount;
    bid.total = bidTotal;
  });

  return { asks, bids };
};

const Exchange = () => {
  const [selectedPair, setSelectedPair] = useState('eth-usdt');
  const [timeframe, setTimeframe] = useState('1D');
  const [chartType, setChartType] = useState('candles');
  const [tabValue, setTabValue] = useState(0);
  const [candlestickData, setCandlestickData] = useState([]);
  const [orderBook, setOrderBook] = useState({ asks: [], bids: [] });
  const [savedNews, setSavedNews] = useState([]);
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [hoveredCandle, setHoveredCandle] = useState(null);
  const [chartAnimation, setChartAnimation] = useState(true);

  // Get crypto symbol from pair
  const getCryptoSymbol = (pairId) => {
    const symbolMap = {
      'eth-usdt': 'ETH',
      'btc-usdt': 'BTC',
      'sol-usdt': 'SOL',
      'avax-usdt': 'AVAX',
      'matic-usdt': 'MATIC',
      'link-usdt': 'LINK'
    };
    return symbolMap[pairId] || 'ETH';
  };

  // Initialize data
  useEffect(() => {
    setIsChartLoading(true);
    const symbol = getCryptoSymbol(selectedPair);

    // Simulate loading delay
    setTimeout(() => {
      setCandlestickData(generateCandlestickData(symbol));
      setOrderBook(generateOrderBook());
      setIsChartLoading(false);
    }, 500);

    // Set up real-time data simulation
    const interval = setInterval(() => {
      setCandlestickData(prevData => {
        if (prevData.length === 0) return prevData;

        // Update the last candle to simulate real-time updates
        const updatedData = [...prevData];
        const lastCandle = {...updatedData[updatedData.length - 1]};

        // Random price movement
        const priceChange = lastCandle.close * (Math.random() * 0.01 - 0.005);
        lastCandle.close = lastCandle.close + priceChange;

        // Update high/low if needed
        if (lastCandle.close > lastCandle.high) {
          lastCandle.high = lastCandle.close;
        }
        if (lastCandle.close < lastCandle.low) {
          lastCandle.low = lastCandle.close;
        }

        // Update volume
        lastCandle.volume += Math.floor(Math.random() * 50);

        updatedData[updatedData.length - 1] = lastCandle;
        return updatedData;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [selectedPair]);

  // Handle pair change
  const handlePairChange = (event) => {
    setIsChartLoading(true);
    setSelectedPair(event.target.value);
    setChartAnimation(true);

    // Reset hover state
    setHoveredCandle(null);

    // In a real app, we would fetch new data for the selected pair
    const symbol = getCryptoSymbol(event.target.value);

    // Simulate loading delay
    setTimeout(() => {
      setCandlestickData(generateCandlestickData(symbol));
      setOrderBook(generateOrderBook());
      setIsChartLoading(false);

      // Disable animation after initial load
      setTimeout(() => {
        setChartAnimation(false);
      }, 1000);
    }, 500);
  };

  // Handle timeframe change
  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
    setIsChartLoading(true);
    setChartAnimation(true);

    // Reset hover state
    setHoveredCandle(null);

    // In a real app, we would fetch new data for the selected timeframe
    let days;
    switch (newTimeframe) {
      case '1H': days = 24; break;
      case '1D': days = 30; break;
      case '1W': days = 90; break;
      case '1M': days = 180; break;
      default: days = 30;
    }

    const symbol = getCryptoSymbol(selectedPair);

    // Simulate loading delay
    setTimeout(() => {
      setCandlestickData(generateCandlestickData(symbol, days));
      setIsChartLoading(false);

      // Disable animation after initial load
      setTimeout(() => {
        setChartAnimation(false);
      }, 1000);
    }, 300);
  };

  // Handle chart type change
  const handleChartTypeChange = (newType) => {
    if (chartType !== newType) {
      setChartType(newType);
      setChartAnimation(true);

      // Disable animation after transition
      setTimeout(() => {
        setChartAnimation(false);
      }, 1000);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle candle hover
  const handleCandleHover = (candle) => {
    setHoveredCandle(candle);
  };

  // Handle candle hover exit
  const handleCandleHoverExit = () => {
    setHoveredCandle(null);
  };

  // Toggle save news
  const toggleSaveNews = (newsId) => {
    if (savedNews.includes(newsId)) {
      setSavedNews(savedNews.filter(id => id !== newsId));
    } else {
      setSavedNews([...savedNews, newsId]);
    }
  };

  // Get current pair data
  const currentPair = tradingPairs.find(pair => pair.id === selectedPair);

  // Render candlestick chart with animations
  const renderCandlestickChart = () => {
    if (candlestickData.length === 0) {
      return (
        <div className="chart-container">
          <div className="chart-loading">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading chart data...</div>
          </div>
        </div>
      );
    }

    // Calculate min and max prices for scaling with padding
    const minPrice = Math.min(...candlestickData.map(d => d.low)) * 0.995;
    const maxPrice = Math.max(...candlestickData.map(d => d.high)) * 1.005;
    const priceRange = maxPrice - minPrice;
    const chartHeight = 400;
    const chartWidth = 800;
    const candleWidth = Math.min(chartWidth / candlestickData.length, 15);
    const chartPadding = 40; // Padding for labels

    // Scale price to chart height
    const scalePrice = (price) => {
      return chartHeight - chartPadding - ((price - minPrice) / priceRange) * (chartHeight - 2 * chartPadding);
    };

    // Generate price lines
    const priceLines = [];
    const numPriceLines = 5;
    for (let i = 0; i < numPriceLines; i++) {
      const price = minPrice + (priceRange / (numPriceLines - 1)) * i;
      priceLines.push(price);
    }

    // Format price based on the range
    const formatPrice = (price) => {
      if (price >= 1000) {
        return price.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });
      } else if (price >= 100) {
        return price.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      } else if (price >= 1) {
        return price.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 4
        });
      } else {
        return price.toLocaleString('en-US', {
          minimumFractionDigits: 4,
          maximumFractionDigits: 6
        });
      }
    };

    // Get crypto symbol from the selected pair
    const symbol = getCryptoSymbol(selectedPair);

    // Determine gradient colors based on crypto
    const getGradientColors = (symbol) => {
      const colorMap = {
        'ETH': { start: '#6B8CEF', end: '#3355FF' },
        'BTC': { start: '#F7931A', end: '#E2761B' },
        'SOL': { start: '#9945FF', end: '#6A1B9A' },
        'AVAX': { start: '#E84142', end: '#B71C1C' },
        'MATIC': { start: '#8247E5', end: '#4A148C' },
        'LINK': { start: '#2A5ADA', end: '#1A237E' }
      };
      return colorMap[symbol] || { start: '#00ffff', end: '#0088ff' };
    };

    const gradientColors = getGradientColors(symbol);

    // Determine if we should show the tooltip
    const showTooltip = hoveredCandle !== null;

    // Calculate tooltip position
    const tooltipX = hoveredCandle ?
      candlestickData.findIndex(c => c.timestamp === hoveredCandle.timestamp) * candleWidth : 0;
    const tooltipY = hoveredCandle ?
      scalePrice(hoveredCandle.close) : 0;

    return (
      <div className="chart-container">
        {isChartLoading ? (
          <div className="chart-loading">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading chart data...</div>
          </div>
        ) : (
          <>
            <svg width="100%" height="100%" viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="none">
              <defs>
                {/* Gradient for up candles */}
                <linearGradient id="upCandleGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#00ff88" stopOpacity="1" />
                  <stop offset="100%" stopColor="#00cc66" stopOpacity="1" />
                </linearGradient>

                {/* Gradient for down candles */}
                <linearGradient id="downCandleGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#ff4d4d" stopOpacity="1" />
                  <stop offset="100%" stopColor="#cc0000" stopOpacity="1" />
                </linearGradient>

                {/* Gradient for volume bars */}
                <linearGradient id="volumeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={gradientColors.start} stopOpacity="0.5" />
                  <stop offset="100%" stopColor={gradientColors.end} stopOpacity="0.1" />
                </linearGradient>

                {/* Glow filter for hover effect */}
                <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
              </defs>

              {/* Chart background with grid */}
              <rect
                x="0"
                y="0"
                width={chartWidth}
                height={chartHeight}
                fill="rgba(0, 0, 0, 0.2)"
                rx="8"
                ry="8"
              />

              {/* Price lines */}
              {priceLines.map((price, index) => (
                <g key={`priceline-${index}`}>
                  <line
                    x1="0"
                    y1={scalePrice(price)}
                    x2={chartWidth}
                    y2={scalePrice(price)}
                    stroke="rgba(255, 255, 255, 0.1)"
                    strokeWidth="1"
                    strokeDasharray={index === 0 || index === numPriceLines - 1 ? "none" : "5,5"}
                  />
                  <text
                    x={chartWidth - 50}
                    y={scalePrice(price) - 5}
                    fill="rgba(255, 255, 255, 0.7)"
                    fontSize="12"
                    fontWeight={index === 0 || index === numPriceLines - 1 ? "bold" : "normal"}
                  >
                    ${formatPrice(price)}
                  </text>
                </g>
              ))}

              {/* Volume bars in background */}
              <g className="volume-bars">
                {candlestickData.map((candle, index) => {
                  const x = index * candleWidth;
                  const maxVolume = Math.max(...candlestickData.map(d => d.volume));
                  const volumeHeight = (candle.volume / maxVolume) * (chartHeight * 0.2);
                  const volumeY = chartHeight - volumeHeight;

                  return (
                    <rect
                      key={`volume-${index}`}
                      x={x + (candleWidth * 0.1)}
                      y={volumeY}
                      width={candleWidth * 0.8}
                      height={volumeHeight}
                      fill="url(#volumeGradient)"
                      opacity={chartAnimation ? 0 : 0.7}
                      className={chartAnimation ? "animated-volume" : ""}
                      style={{
                        animationDelay: `${index * 20}ms`,
                        transform: chartAnimation ? 'scaleY(0)' : 'scaleY(1)',
                        transformOrigin: 'bottom'
                      }}
                    />
                  );
                })}
              </g>

              {/* Candlesticks */}
              {candlestickData.map((candle, index) => {
                const isUp = candle.close >= candle.open;
                const x = index * candleWidth;
                const candleBodyWidth = candleWidth * 0.8;
                const wickWidth = 1.5;

                // Calculate body dimensions
                const openY = scalePrice(candle.open);
                const closeY = scalePrice(candle.close);
                const highY = scalePrice(candle.high);
                const lowY = scalePrice(candle.low);

                const bodyTop = Math.min(openY, closeY);
                const bodyHeight = Math.abs(openY - closeY) || 1; // Ensure minimum height

                // Determine if this candle is hovered
                const isHovered = hoveredCandle && hoveredCandle.timestamp === candle.timestamp;

                return (
                  <g
                    key={`candle-${index}`}
                    className={chartAnimation ? "animated-candle" : ""}
                    style={{
                      animationDelay: `${index * 20}ms`,
                      opacity: chartAnimation ? 0 : 1,
                      transform: chartAnimation ? 'translateY(10px)' : 'translateY(0)'
                    }}
                    onMouseEnter={() => handleCandleHover(candle)}
                    onMouseLeave={handleCandleHoverExit}
                  >
                    {/* Wick */}
                    <line
                      x1={x + candleWidth/2}
                      y1={highY}
                      x2={x + candleWidth/2}
                      y2={lowY}
                      stroke={isUp ? "#00ff88" : "#ff4d4d"}
                      strokeWidth={wickWidth}
                      strokeOpacity={isHovered ? 1 : 0.8}
                      filter={isHovered ? "url(#glow)" : "none"}
                    />

                    {/* Candle body */}
                    <rect
                      x={x + (candleWidth - candleBodyWidth)/2}
                      y={bodyTop}
                      width={candleBodyWidth}
                      height={bodyHeight}
                      rx={1}
                      ry={1}
                      fill={isUp ? "url(#upCandleGradient)" : "url(#downCandleGradient)"}
                      stroke={isUp ? "#00ff88" : "#ff4d4d"}
                      strokeWidth={isHovered ? 2 : 1}
                      filter={isHovered ? "url(#glow)" : "none"}
                    />
                  </g>
                );
              })}

              {/* Current price line (last candle) */}
              {candlestickData.length > 0 && (
                <g>
                  <line
                    x1="0"
                    y1={scalePrice(candlestickData[candlestickData.length - 1].close)}
                    x2={chartWidth}
                    y2={scalePrice(candlestickData[candlestickData.length - 1].close)}
                    stroke="#00ffff"
                    strokeWidth="1"
                    strokeDasharray="5,3"
                  />
                  <text
                    x={chartWidth - 10}
                    y={scalePrice(candlestickData[candlestickData.length - 1].close) - 10}
                    fill="#00ffff"
                    fontSize="12"
                    fontWeight="bold"
                    textAnchor="end"
                  >
                    ${formatPrice(candlestickData[candlestickData.length - 1].close)}
                  </text>
                </g>
              )}
            </svg>

            {/* Tooltip */}
            {showTooltip && (
              <div
                className="chart-tooltip"
                style={{
                  left: `${tooltipX + candleWidth/2}px`,
                  top: `${tooltipY - 80}px`
                }}
              >
                <div className="tooltip-date">
                  {new Date(hoveredCandle.timestamp).toLocaleDateString()} {new Date(hoveredCandle.timestamp).toLocaleTimeString()}
                </div>
                <div className="tooltip-price">
                  <div className="tooltip-label">Open:</div>
                  <div className="tooltip-value">${formatPrice(hoveredCandle.open)}</div>
                </div>
                <div className="tooltip-price">
                  <div className="tooltip-label">High:</div>
                  <div className="tooltip-value">${formatPrice(hoveredCandle.high)}</div>
                </div>
                <div className="tooltip-price">
                  <div className="tooltip-label">Low:</div>
                  <div className="tooltip-value">${formatPrice(hoveredCandle.low)}</div>
                </div>
                <div className="tooltip-price">
                  <div className="tooltip-label">Close:</div>
                  <div className="tooltip-value">${formatPrice(hoveredCandle.close)}</div>
                </div>
                <div className="tooltip-volume">
                  <div className="tooltip-label">Volume:</div>
                  <div className="tooltip-value">{hoveredCandle.volume.toLocaleString()}</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Render line chart with animations
  const renderLineChart = () => {
    if (candlestickData.length === 0) {
      return (
        <div className="chart-container">
          <div className="chart-loading">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading chart data...</div>
          </div>
        </div>
      );
    }

    // Calculate min and max prices for scaling with padding
    const minPrice = Math.min(...candlestickData.map(d => d.low)) * 0.995;
    const maxPrice = Math.max(...candlestickData.map(d => d.high)) * 1.005;
    const priceRange = maxPrice - minPrice;
    const chartHeight = 400;
    const chartWidth = 800;
    const chartPadding = 40; // Padding for labels

    // Scale price to chart height
    const scalePrice = (price) => {
      return chartHeight - chartPadding - ((price - minPrice) / priceRange) * (chartHeight - 2 * chartPadding);
    };

    // Format price based on the range
    const formatPrice = (price) => {
      if (price >= 1000) {
        return price.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });
      } else if (price >= 100) {
        return price.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      } else if (price >= 1) {
        return price.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 4
        });
      } else {
        return price.toLocaleString('en-US', {
          minimumFractionDigits: 4,
          maximumFractionDigits: 6
        });
      }
    };

    // Generate points for the line
    const points = candlestickData.map((data, index) => {
      const x = (index / (candlestickData.length - 1)) * chartWidth;
      const y = scalePrice(data.close);
      return `${x},${y}`;
    }).join(' ');

    // Generate area points (line + bottom)
    const areaPoints = `0,${chartHeight} ${points} ${chartWidth},${chartHeight}`;

    // Generate price lines
    const priceLines = [];
    const numPriceLines = 5;
    for (let i = 0; i < numPriceLines; i++) {
      const price = minPrice + (priceRange / (numPriceLines - 1)) * i;
      priceLines.push(price);
    }

    // Get crypto symbol from the selected pair
    const symbol = getCryptoSymbol(selectedPair);

    // Determine gradient colors based on crypto
    const getGradientColors = (symbol) => {
      const colorMap = {
        'ETH': { start: '#6B8CEF', end: '#3355FF', area: 'rgba(107, 140, 239, 0.3)' },
        'BTC': { start: '#F7931A', end: '#E2761B', area: 'rgba(247, 147, 26, 0.3)' },
        'SOL': { start: '#9945FF', end: '#6A1B9A', area: 'rgba(153, 69, 255, 0.3)' },
        'AVAX': { start: '#E84142', end: '#B71C1C', area: 'rgba(232, 65, 66, 0.3)' },
        'MATIC': { start: '#8247E5', end: '#4A148C', area: 'rgba(130, 71, 229, 0.3)' },
        'LINK': { start: '#2A5ADA', end: '#1A237E', area: 'rgba(42, 90, 218, 0.3)' }
      };
      return colorMap[symbol] || { start: '#00ffff', end: '#0088ff', area: 'rgba(0, 255, 255, 0.3)' };
    };

    const gradientColors = getGradientColors(symbol);

    // Find closest data point to mouse position
    const findClosestPoint = (mouseX) => {
      if (!candlestickData.length) return null;

      const pointWidth = chartWidth / candlestickData.length;
      const index = Math.min(
        Math.floor(mouseX / pointWidth),
        candlestickData.length - 1
      );

      return candlestickData[index];
    };

    // Handle mouse move on chart
    const handleChartMouseMove = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const point = findClosestPoint(mouseX);
      if (point) {
        handleCandleHover(point);
      }
    };

    // Handle mouse leave
    const handleChartMouseLeave = () => {
      handleCandleHoverExit();
    };

    // Determine if we should show the tooltip
    const showTooltip = hoveredCandle !== null;

    // Calculate tooltip position
    const tooltipX = hoveredCandle ?
      candlestickData.findIndex(c => c.timestamp === hoveredCandle.timestamp) * (chartWidth / candlestickData.length) : 0;
    const tooltipY = hoveredCandle ?
      scalePrice(hoveredCandle.close) : 0;

    // Calculate the stroke-dasharray for animation
    const pathLength = chartWidth * 2; // Approximate path length

    return (
      <div className="chart-container">
        {isChartLoading ? (
          <div className="chart-loading">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading chart data...</div>
          </div>
        ) : (
          <>
            <svg
              width="100%"
              height="100%"
              viewBox={`0 0 ${chartWidth} ${chartHeight}`}
              preserveAspectRatio="none"
              onMouseMove={handleChartMouseMove}
              onMouseLeave={handleChartMouseLeave}
            >
              <defs>
                {/* Line gradient */}
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={gradientColors.end} />
                  <stop offset="100%" stopColor={gradientColors.start} />
                </linearGradient>

                {/* Area gradient */}
                <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={gradientColors.area} />
                  <stop offset="100%" stopColor="rgba(0, 0, 0, 0)" />
                </linearGradient>

                {/* Glow filter for line */}
                <filter id="lineGlow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="2" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>

                {/* Clip path for area animation */}
                <clipPath id="areaClip">
                  <rect
                    x="0"
                    y="0"
                    width={chartAnimation ? "0%" : "100%"}
                    height={chartHeight}
                    className="area-clip-rect"
                  >
                    {chartAnimation && (
                      <animate
                        attributeName="width"
                        from="0%"
                        to="100%"
                        dur="1s"
                        fill="freeze"
                        begin="0s"
                      />
                    )}
                  </rect>
                </clipPath>
              </defs>

              {/* Chart background with grid */}
              <rect
                x="0"
                y="0"
                width={chartWidth}
                height={chartHeight}
                fill="rgba(0, 0, 0, 0.2)"
                rx="8"
                ry="8"
              />

              {/* Price lines */}
              {priceLines.map((price, index) => (
                <g key={`priceline-${index}`}>
                  <line
                    x1="0"
                    y1={scalePrice(price)}
                    x2={chartWidth}
                    y2={scalePrice(price)}
                    stroke="rgba(255, 255, 255, 0.1)"
                    strokeWidth="1"
                    strokeDasharray={index === 0 || index === numPriceLines - 1 ? "none" : "5,5"}
                  />
                  <text
                    x={chartWidth - 50}
                    y={scalePrice(price) - 5}
                    fill="rgba(255, 255, 255, 0.7)"
                    fontSize="12"
                    fontWeight={index === 0 || index === numPriceLines - 1 ? "bold" : "normal"}
                  >
                    ${formatPrice(price)}
                  </text>
                </g>
              ))}

              {/* Area under the line with animation */}
              <g clipPath="url(#areaClip)">
                <polygon
                  points={areaPoints}
                  fill="url(#areaGradient)"
                  className="area-path"
                />
              </g>

              {/* Line with animation */}
              <polyline
                points={points}
                fill="none"
                stroke="url(#lineGradient)"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                filter="url(#lineGlow)"
                className="line-path"
                style={{
                  strokeDasharray: chartAnimation ? pathLength : 'none',
                  strokeDashoffset: chartAnimation ? pathLength : 0,
                  animation: chartAnimation ? 'dash 1.5s ease-in-out forwards' : 'none'
                }}
              />

              {/* Data points with animation */}
              {candlestickData.filter((_, i) => i % 5 === 0 || i === candlestickData.length - 1).map((data, index) => {
                const x = (index * 5 / Math.ceil(candlestickData.length / 5)) * chartWidth;
                const actualIndex = index * 5;
                const isLast = actualIndex >= candlestickData.length - 5;

                // Determine if this point is hovered
                const isHovered = hoveredCandle && hoveredCandle.timestamp === data.timestamp;

                return (
                  <g
                    key={`point-${index}`}
                    className="data-point"
                    style={{
                      animation: chartAnimation ? `fadeIn 0.3s ease-in-out forwards ${index * 0.05 + 0.5}s` : 'none',
                      opacity: chartAnimation ? 0 : 1
                    }}
                  >
                    <circle
                      cx={x}
                      cy={scalePrice(data.close)}
                      r={isHovered ? 6 : isLast ? 5 : 3}
                      fill={isLast ? "#ffffff" : gradientColors.start}
                      stroke={isLast ? gradientColors.start : "none"}
                      strokeWidth={isLast ? 2 : 0}
                      filter={isHovered || isLast ? "url(#lineGlow)" : "none"}
                      className="point-circle"
                    />
                  </g>
                );
              })}

              {/* Current price line (last point) */}
              {candlestickData.length > 0 && (
                <g>
                  <line
                    x1="0"
                    y1={scalePrice(candlestickData[candlestickData.length - 1].close)}
                    x2={chartWidth}
                    y2={scalePrice(candlestickData[candlestickData.length - 1].close)}
                    stroke={gradientColors.start}
                    strokeWidth="1"
                    strokeDasharray="5,3"
                    className="current-price-line"
                    style={{
                      opacity: chartAnimation ? 0 : 1,
                      animation: chartAnimation ? 'fadeIn 0.5s ease-in-out forwards 1s' : 'none'
                    }}
                  />
                  <text
                    x={chartWidth - 10}
                    y={scalePrice(candlestickData[candlestickData.length - 1].close) - 10}
                    fill={gradientColors.start}
                    fontSize="12"
                    fontWeight="bold"
                    textAnchor="end"
                    className="current-price-text"
                    style={{
                      opacity: chartAnimation ? 0 : 1,
                      animation: chartAnimation ? 'fadeIn 0.5s ease-in-out forwards 1s' : 'none'
                    }}
                  >
                    ${formatPrice(candlestickData[candlestickData.length - 1].close)}
                  </text>
                </g>
              )}

              {/* Hover line */}
              {hoveredCandle && (
                <line
                  x1={tooltipX}
                  y1="0"
                  x2={tooltipX}
                  y2={chartHeight}
                  stroke="rgba(255, 255, 255, 0.3)"
                  strokeWidth="1"
                  strokeDasharray="5,5"
                />
              )}
            </svg>

            {/* Tooltip */}
            {showTooltip && (
              <div
                className="chart-tooltip"
                style={{
                  position: 'absolute',
                  left: `${tooltipX}px`,
                  top: `${tooltipY - 80}px`,
                  transform: 'translateX(-50%)'
                }}
              >
                <div className="tooltip-date">
                  {new Date(hoveredCandle.timestamp).toLocaleDateString()} {new Date(hoveredCandle.timestamp).toLocaleTimeString()}
                </div>
                <div className="tooltip-price">
                  <div className="tooltip-label">Price:</div>
                  <div className="tooltip-value">${formatPrice(hoveredCandle.close)}</div>
                </div>
                <div className="tooltip-volume">
                  <div className="tooltip-label">Volume:</div>
                  <div className="tooltip-value">{hoveredCandle.volume.toLocaleString()}</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="exchange-container">
      {/* Cryptocurrency Price Overview */}
      <Box mb={4}>
        <CryptoPriceOverview />
      </Box>

      <Typography variant="h6" className="section-title">Trading Chart</Typography>

      <Grid container spacing={3}>
        {/* Trading pair selector and price info */}
        <Grid item xs={12}>
          <Paper className="exchange-header">
            <Box className="pair-selector">
              <FormControl variant="outlined" size="small">
                <Select
                  value={selectedPair}
                  onChange={handlePairChange}
                  className="pair-select"
                >
                  {tradingPairs.map(pair => (
                    <MenuItem key={pair.id} value={pair.id}>
                      {pair.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box className="current-price">
                <Typography variant="h4">
                  ${currentPair.price.toLocaleString()}
                </Typography>
                <Chip
                  icon={currentPair.change >= 0 ? <TrendingUp /> : <TrendingDown />}
                  label={`${currentPair.change >= 0 ? '+' : ''}${currentPair.change}%`}
                  className={`price-change ${currentPair.change >= 0 ? 'positive' : 'negative'}`}
                />
              </Box>
            </Box>

            <Box className="chart-controls">
              <Box className="timeframe-selector">
                {['1H', '1D', '1W', '1M'].map(tf => (
                  <Button
                    key={tf}
                    className={`timeframe-button ${timeframe === tf ? 'active' : ''}`}
                    onClick={() => handleTimeframeChange(tf)}
                  >
                    {tf}
                  </Button>
                ))}
              </Box>

              <Box className="chart-type-selector">
                <Button
                  className={`chart-type-button ${chartType === 'candles' ? 'active' : ''}`}
                  onClick={() => handleChartTypeChange('candles')}
                >
                  Candlestick
                </Button>
                <Button
                  className={`chart-type-button ${chartType === 'line' ? 'active' : ''}`}
                  onClick={() => handleChartTypeChange('line')}
                >
                  Line
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Main chart */}
        <Grid item xs={12} md={8}>
          <Paper className="chart-paper">
            {chartType === 'candles' ? renderCandlestickChart() : renderLineChart()}
          </Paper>
        </Grid>

        {/* Right sidebar with order book and news */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            {/* Order book */}
            <Grid item xs={12}>
              <Paper className="order-book-paper">
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  className="order-book-tabs"
                >
                  <Tab label="Order Book" />
                  <Tab label="Market Trades" />
                </Tabs>

                <Box className="tab-content">
                  {tabValue === 0 ? (
                    <Box className="order-book">
                      <Box className="order-book-header">
                        <Typography className="column-header price">Price</Typography>
                        <Typography className="column-header amount">Amount</Typography>
                        <Typography className="column-header total">Total</Typography>
                      </Box>

                      <Box className="asks">
                        {orderBook.asks.slice(0, 5).map((ask, index) => (
                          <Box key={index} className="order-row ask">
                            <Typography className="price">${ask.price.toFixed(2)}</Typography>
                            <Typography className="amount">{ask.amount.toFixed(4)}</Typography>
                            <Typography className="total">{ask.total.toFixed(4)}</Typography>
                            <Box
                              className="depth-visualization"
                              style={{ width: `${(ask.total / orderBook.asks[orderBook.asks.length - 1].total) * 100}%` }}
                            ></Box>
                          </Box>
                        ))}
                      </Box>

                      <Box className="spread">
                        <Typography>
                          Spread: ${(orderBook.asks[0]?.price - orderBook.bids[0]?.price).toFixed(2)}
                          ({((orderBook.asks[0]?.price - orderBook.bids[0]?.price) / orderBook.asks[0]?.price * 100).toFixed(2)}%)
                        </Typography>
                      </Box>

                      <Box className="bids">
                        {orderBook.bids.slice(0, 5).map((bid, index) => (
                          <Box key={index} className="order-row bid">
                            <Typography className="price">${bid.price.toFixed(2)}</Typography>
                            <Typography className="amount">{bid.amount.toFixed(4)}</Typography>
                            <Typography className="total">{bid.total.toFixed(4)}</Typography>
                            <Box
                              className="depth-visualization"
                              style={{ width: `${(bid.total / orderBook.bids[orderBook.bids.length - 1].total) * 100}%` }}
                            ></Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Box className="market-trades">
                      <Box className="trades-header">
                        <Typography className="column-header">Price</Typography>
                        <Typography className="column-header">Amount</Typography>
                        <Typography className="column-header">Time</Typography>
                      </Box>

                      <Box className="trades-list">
                        {Array(8).fill(0).map((_, index) => {
                          const isBuy = Math.random() > 0.5;
                          return (
                            <Box key={index} className={`trade-row ${isBuy ? 'buy' : 'sell'}`}>
                              <Typography className="price">
                                ${(3500 + (Math.random() - 0.5) * 20).toFixed(2)}
                              </Typography>
                              <Typography className="amount">
                                {(Math.random() * 2 + 0.1).toFixed(4)}
                              </Typography>
                              <Typography className="time">
                                {new Date(Date.now() - Math.random() * 3600000).toLocaleTimeString()}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Market News - Compact Version */}
            <Grid item xs={12}>
              <Paper className="news-paper">
                <Box className="news-header">
                  <Typography variant="h6">Market News</Typography>
                  <Button
                    startIcon={<Refresh />}
                    className="refresh-button"
                    size="small"
                  >
                    Refresh
                  </Button>
                </Box>

                <Box className="news-list-compact">
                  {newsItems.map(news => (
                    <Box key={news.id} className="news-item-compact">
                      <Box className="news-content-compact">
                        <Typography className="news-title-compact">
                          {news.title}
                        </Typography>

                        <Box className="news-meta-compact">
                          <Typography className="news-source-compact">
                            {news.source}
                          </Typography>
                          <Typography className="news-time-compact">
                            {news.time}
                          </Typography>
                          {news.important && (
                            <Chip
                              label="Important"
                              size="small"
                              className="important-chip-compact"
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default Exchange;
